import type { UpdateApplicationVersionByIdV2MutationRequestApi } from '@ingenico/api-sdk-device-management-private-v2';
import type { VersionStatus } from '../../pages/application-details-page/ApplicationDetailsPage.model';
import type { ApplicationPaginationData } from './application.model';
import type { ArtifactComponentTypeEnum, ArtifactTypeEnum } from './artifact.model';
import type { ComponentVersionDependencyMetadataFront } from './componentVersionDependencyMetadata.model';
import type { MappedCreateDownloadRules } from './downloadRules.model';
import type { PaginatedListResponseFront } from './pagination.model';
import type { EnumVersionPublicationStatus } from './systemPackageVersion.model';
import type { TemplateLocation } from './template.model';
//----------------------APPLICATION VERSION----------------------

export const versionPublicationStatus = {
  DEPRECATED: 'DEPRECATED',
  PUBLISHED: 'PUBLISHED',
} as const;
export type VersionPublicationStatus = (typeof versionPublicationStatus)[keyof typeof versionPublicationStatus];

export interface ApplicationVersionResourceFront {
  readonly applicationDependencyVersionIds?: string[];
  readonly applicationId?: string;
  readonly artifactIds?: string[];
  readonly compatibleOsVersionIds?: string[];
  readonly creationDate?: Date;
  readonly id?: string;
  readonly longDescription?: string;
  readonly publisherId?: string;
  readonly shortDescription?: string;
  readonly size?: number;
  readonly status?: VersionPublicationStatus;
  readonly systemPackageDependencyVersionIds?: string[];
  readonly templateIds?: string[];
  readonly versionNumber?: string;
}

export type BffApplicationVersionResourceFront = ApplicationVersionResourceFront & {
  readonly applicationDependencyVersions: ComponentVersionDependencyMetadataFront[];
  readonly compatibleOsVersions: ComponentVersionDependencyMetadataFront[];
  readonly systemPackageDependencyVersions: ComponentVersionDependencyMetadataFront[];
  readonly downloadRules?: MappedCreateDownloadRules[];
};

export type ApplicationVersionListResponseFront = PaginatedListResponseFront & {
  readonly data: ApplicationVersionResourceFront[];
};

export interface CreateApplicationVersionArtifacts {
  readonly id: string;
  readonly order: number;
  readonly targetLocation?: string;
}

//----------------------SYSTEM PACKAGE VERSION----------------------

export interface SystemPackageVersionResourceFront {
  readonly systemPackageId?: string;
  readonly artifactIds?: string[];
  readonly compatibleOsVersionIds?: string[];
  readonly creationDate?: Date;
  readonly id?: string;
  readonly longDescription?: string;
  readonly publisherId?: string;
  readonly shortDescription?: string;
  readonly status?: VersionPublicationStatus;
  readonly templateIds?: string[];
  readonly versionNumber?: string;
}

export type SystemPackageVersionListResponseFront = PaginatedListResponseFront & {
  readonly data: SystemPackageVersionResourceFront[];
};

//----------------------TEMPLATE----------------------

export interface TemplateResourceBasicFront {
  readonly componentType?: string;
  readonly createdBy?: string;
  readonly creationDate?: Date;
  readonly description?: string;
  readonly id?: string;
  readonly name?: string;
  readonly targetLocation?: string;
  readonly templateFileId?: string;
  readonly templateFileName?: string;
  readonly templateFileVersion?: string;
}

export type TemplateResourceListResponseFront = PaginatedListResponseFront & {
  readonly data: TemplateResourceBasicFront[];
};

//----------------------PUT APPLICATION VERSION----------------------

export interface PutApplicationVersion {
  readonly applicationVersionId: string;
  readonly body: UpdateApplicationVersionByIdV2MutationRequestApi;
}

//----------------------ARTIFACTS----------------------

export enum ApplicationResourceArtifactComponentTypeEnum {
  APPLICATION = 'APPLICATION',
}

export interface ApplicationResourceArtifact {
  readonly artifactType: ArtifactTypeEnum;
  readonly checksum: string;
  readonly componentType: ArtifactComponentTypeEnum;
  readonly id: string;
  readonly mimeType: string;
  readonly name: string;
  readonly repositoryId: string;
  readonly size: number;
}

export type ApplicationVersionArtifactsListResponseFront = PaginatedListResponseFront & {
  readonly artifacts: ApplicationResourceArtifact[];
};

interface ComponentVersionDependencyMetadataResource {
  readonly name?: string;
  readonly versionId?: string;
  readonly versionNumber?: string;
}

export interface MappedApplicationVersionResourceReadV2Api {
  readonly creationDate: Date | string;
  readonly status: VersionStatus;
  readonly id: string;
  readonly size: number;
  readonly applicationDependencyVersionIds?: string[];
  readonly applicationId?: string;
  readonly artifactIds?: string[];
  readonly compatibleOsVersionIds?: string[];
  readonly longDescription?: string;
  readonly publisherId?: string;
  readonly shortDescription?: string;
  readonly systemPackageDependencyVersionIds?: string[];
  readonly templates?: TemplateLocation[];
  readonly versionNumber?: string;
}

export interface MappedBffApplicationVersionArtifactResource {
  readonly id: string;
  readonly order: number;
  readonly targetLocation?: string;
}

export interface MappedBffApplicationVersionResourceReadV2Api {
  readonly creationDate?: Date;
  readonly status?: EnumVersionPublicationStatus;
  readonly id?: string;
  readonly size?: number;
  readonly applicationDependencyVersionIds?: string[];
  readonly applicationId?: string;
  readonly artifacts?: MappedBffApplicationVersionArtifactResource[];
  readonly compatibleOsVersionIds?: string[];
  readonly longDescription?: string;
  readonly publisherId?: string;
  readonly shortDescription?: string;
  readonly systemPackageDependencyVersionIds?: string[];
  readonly templates?: TemplateLocation[];
  readonly versionNumber?: string;
  readonly applicationDependencyVersions: ComponentVersionDependencyMetadataResource[];
  readonly compatibleOsVersions: ComponentVersionDependencyMetadataResource[];
  readonly systemPackageDependencyVersions: ComponentVersionDependencyMetadataResource[];
  readonly downloadRules?: MappedCreateDownloadRules[];
}

export interface MappedApplicationVersionResourceListResponseV2Api {
  readonly pagination: ApplicationPaginationData;
  readonly data: MappedApplicationVersionResourceReadV2Api[];
}

interface MappedUpdateApplicationVersionArtifact {
  readonly id: string;
  readonly order: number;
  readonly targetLocation?: string;
}

export interface MappedUpdateApplicationVersionResourceV2Api {
  readonly applicationDependencyVersionIds?: string[];
  readonly applicationId?: string;
  readonly artifacts?: MappedUpdateApplicationVersionArtifact[];
  readonly compatibleOsVersionIds?: string[];
  readonly creationDate?: Date;
  readonly id?: string;
  readonly longDescription?: string;
  readonly publisherId?: string;
  readonly shortDescription?: string;
  readonly size?: number;
  readonly status?: EnumVersionPublicationStatus;
  readonly templates?: TemplateLocation[];
  readonly versionNumber?: string;
  readonly downloadRules?: MappedCreateDownloadRules[];
}
